<template>
    <BasePanel
        headline="lang_CONTRACTS_PUBLISHING_CAPTION_AGREEMENT_NUMBERS"
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="itemsPerPage"
                :page.sync="currentPage"
            >
                <template v-slot:top>
                    <!-- columns mirror the table column widths, so each filter sits above its column -->
                    <v-row no-gutters class="pb-6" align="center">
                        <v-col cols="4" class="px-4">
                            <v-select
                                v-model="sourceFilter"
                                :items="sourceFilterOptions"
                                :label="$t('lang_GENERIC_CAPTION_SOURCE')"
                                hide-details
                                :alvqa="alvqa + '-dropdown-source-filter'"
                            ></v-select>
                        </v-col>
                        <v-col cols="4" class="px-4" v-if="subCatalogues.length > 0">
                            <v-select
                                v-model="catalogueFilter"
                                :items="catalogueFilterOptions"
                                :label="$t('lang_GENERIC_CAPTION_CATALOGUE')"
                                hide-details
                                :alvqa="alvqa + '-dropdown-catalogue-filter'"
                            ></v-select>
                        </v-col>
                        <v-spacer></v-spacer>
                        <v-col cols="3" class="px-4">
                            <BaseButton
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                            />
                        </v-col>
                    </v-row>
                </template>

                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

                <template v-slot:[`item.subcatalogId`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-catalogue'">
                        {{ getCatalogueName(item.subcatalogId) }}
                    </div>
                </template>
                <template v-slot:item.sourceAlvId="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-source-alv-id'">
                        {{ getSourceName(item.sourceAlvId) }}
                    </div>
                </template>
                <template v-slot:[`item.agreementNumber`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-agreement-number'">
                        {{ item.agreementNumber }}
                    </div>
                </template>
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + '-list-row-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit"
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
            </v-data-table>
            <v-pagination
                v-if="numberOfPages > 1"
                total-visible="10"
                v-model="currentPage"
                :length="numberOfPages"
                color="pagination"
            ></v-pagination>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'

export default {
    name: "PublishingContractsAgreementNumbersEditPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
    },
    data() {
        return {
            alvqa: "contracts-publishing-edit-form-panel-agreement-numbers",
            currentPage: 1,
            catalogueFilter: 'all',
            sourceFilter: 'all'
        }
    },
    computed: {
        // only societies the account is a direct member of, same list as the CWR export recipient
        sourceOptions() {
            const societies = this.$store.getters.getAzSocietiesWhereDirectMember || []
            return societies.map(society => ({ value: society.alvId, text: society.societyAcronym }))
        },
        sourceFilterOptions() {
            return [{ text: this.$t('lang_GENERIC_CAPTION_ALL'), value: 'all' }].concat(this.sourceOptions)
        },
        subCatalogues() { return this.$store.getters.getPublishingContractsSubCatalogues || [] },
        catalogueFilterOptions() {
            return [{ text: this.$t('lang_GENERIC_CAPTION_ALL'), value: 'all' }].concat(this.catalogueOptions)
        },
        catalogueOptions() {
            const subCatalogueCaption = this.$t('lang_PUBLISHING_WORKS_CAPTION_SUBCATALOGUE')
            const options = [{ text: this.mainCatalogueName, value: null }]
            this.subCatalogues.forEach(subCatalogue => {
                options.push({
                    text: subCatalogueCaption + ': ' + this.getLicensorName(subCatalogue.licensor),
                    value: subCatalogue.id
                })
            })
            return options
        },
        mainCatalogueName() {
            const name = this.getLicensorName(this.$store.getters.getPublishingContractsDetailsLicensor)
            const caption = this.$t('lang_CONTRACTS_PUBLISHING_CAPTION_MAIN_CATALOGUE')
            return name ? caption + ': ' + name : caption
        },
        headers() {
            return [
                {
                    text: this.$t("lang_GENERIC_CAPTION_SOURCE"),
                    align: "left",
                    value: "sourceAlvId",
                    class: 'text-no-wrap',
                    width: "33.33%",
                    sortable: false,
                    alvqa: this.alvqa + '-header-source'
                },
                {
                    text: this.$t("lang_GENERIC_CAPTION_CATALOGUE"),
                    align: "left",
                    value: "subcatalogId",
                    class: 'text-no-wrap',
                    width: "33.33%",
                    sortable: false,
                    alvqa: this.alvqa + '-header-catalogue'
                },
                {
                    text: this.$t("lang_CONTRACTS_PUBLISHING_CAPTION_AGREEMENT_NUMBER"),
                    align: "left",
                    value: "agreementNumber",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-agreement-number'
                },
                {
                    text: '',
                    value: 'inlineEdit',
                    align: 'right',
                    width: "70px",
                    sortable: false,
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },
        allRows() { return this.$store.getters.getPublishingContractsAgreementNumbers || [] },
        listItems() {
            return this.allRows
                .filter(row => this.isVisible(row))
                .slice()
                .sort((a, b) => {
                    const bySource = this.getSourceName(a.sourceAlvId).localeCompare(this.getSourceName(b.sourceAlvId))
                    if (bySource !== 0) return bySource
                    return this.getCatalogueName(a.subcatalogId).localeCompare(this.getCatalogueName(b.subcatalogId))
                })
        },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        numberOfPages() { return Math.ceil(this.listItems.length / this.itemsPerPage) },
    },
    watch: {
        numberOfPages(pages) {
            if (this.currentPage > pages) this.currentPage = pages > 0 ? pages : 1
        },
        // a row added through the dialog must not disappear behind an active filter
        allRows(rows, previousRows) {
            if (rows.length <= previousRows.length) return
            const added = rows.find(row => !previousRows.some(previous => previous.id === row.id))
            if (added && !this.isVisible(added)) {
                this.sourceFilter = 'all'
                this.catalogueFilter = 'all'
            }
        }
    },
    methods: {
        isVisible(row) {
            if (this.sourceFilter !== 'all' && row.sourceAlvId !== this.sourceFilter) return false
            if (this.catalogueFilter !== 'all' && (row.subcatalogId || null) !== this.catalogueFilter) return false
            return true
        },
        getLicensorName(licensor) {
            if (!licensor) return ''
            if (licensor.group !== null && licensor.group !== undefined && licensor.group !== '') return licensor.group
            return licensor.gemaName || licensor.name || ''
        },
        getCatalogueName(subcatalogId) {
            const found = this.catalogueOptions.find(option => option.value === (subcatalogId || null))
            return found ? found.text : ''
        },
        getSourceName(alvId) {
            if (!alvId || !this.sourceOptions) return ''
            const found = this.sourceOptions.find(o => o.value === alvId)
            if (found) return found.text + " (" + alvId + ")"
            // membership id of that society was removed in A-Z afterwards, the entry itself stays valid
            return this.$t('lang_GENERIC_CAPTION_NOT_A_MEMBER') + " (" + alvId + ")"
        },
        addNewRow() {
            this.$store.commit('setSelectedPublishingContractsAgreementNumbers', [])
            this.$store.commit('setShowPublishingContractsAgreementNumbersEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultPublishingContractsAgreementNumbers))
            this.$store.commit('setPublishingContractsAgreementNumbersEditingDialogDetails', defaultValues)
        },
        deleteRow(item) {
            this.$store.commit("setSelectedPublishingContractsAgreementNumbers", item)
            this.$store.commit('setDeletionDialogContext', 'SelectedPublishingContractsAgreementNumbers')
            this.$store.commit('setShowDeletionDialog', true)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedPublishingContractsAgreementNumbers', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))
            this.$store.commit('setPublishingContractsAgreementNumbersEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY"))
            this.$store.commit('setShowPublishingContractsAgreementNumbersEditingDialog', true)
        }
    },
    async mounted() {
        await this.$store.dispatch("fetchAzSocietiesWhereDirectMember")
    },
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>
